// src/services/surchargeApiSlice.ts
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithRefreshTokenHandler } from '../authFetchBaseQuery';
import { DealerSurchargeProfiles, HandleDealerSurchargeRequest, HandleDealerSurchargeResponse, SurchargeProfileResponse, PaginationSearchSortingType,
     DealerSurchargeResponse, CreateSurchargeProfile, UpdateSurchargeProfile, CreateSurchargeProfileResponse, UpdateSurchargeProfileResponse } from '../../types/types';
import { IOnboardedDealer } from '../../types/WebHookTypes';
import { API_GET_DEALER_SURCHARGE_PROFILE_BY_DEALERID, API_GET_DEALER_SURCHARGE_PROFILES, API_GET_SURCHARGE_PROFILES, API_POST_HANDLE_SURCHARGE_FOR_DEALER,
     API_POST_ONBOARDED_DEALERS, API_GET_SURCHARGE_PROFILES_By_Pagination, API_POST_CREATE_SURCHARGE_PROFILE,
     API_PUT_UPDATE_SURCHARGE_PROFILE, API_DELETE_SURCHARGE_PROFILE, API_GET_ONBOARDED_DEALERS_BY_MERCHANT } from '../ApiConstants';

/**
 * RTK Query API slice for managing dealer surcharge-related endpoints.
 * Handles queries and mutations for surcharge profiles and dealer assignments.
 */
export const dealerSurchargeApi = createApi({
    reducerPath: 'dealerSurchargeApi',
    baseQuery: baseQueryWithRefreshTokenHandler,
    tagTypes: ['Dealers','DealersWithSurcharge','DealerWithSurcharge'],
    refetchOnMountOrArgChange: true,
    keepUnusedDataFor: 0,
    endpoints: (builder) => ({

        /**
         * Fetches a list of Perseus Pay onboarded dealers from Everest using a query request.
         * @param request The request object containing filter and pagination.
         * @returns List of onboarded dealers.
         */
        getPerseusPayOnboardedDealers: builder.query({
            query: (request: PaginationSearchSortingType) => {
                        return {
                            url: API_POST_ONBOARDED_DEALERS,
                            method: 'POST',
                            body: JSON.stringify(request),
                        };
                    },
            providesTags: ['Dealers'],
        }),

        getOnboardedDealersByMerchant: builder.query<IOnboardedDealer[], string>({
            query: (merchantId: string) => `${API_GET_ONBOARDED_DEALERS_BY_MERCHANT}?merchantId=${merchantId}`,
            providesTags: ['Dealers'],
        }),

        /**
         * Retrieves all available surcharge profiles.
         * @returns Array of surcharge profile response objects.
         */
        getAllSurchargeProfiles: builder.query<SurchargeProfileResponse[], void>({
            query: () => API_GET_SURCHARGE_PROFILES,
        }),
        
        /**
         * Retrieves all available surcharge profiles wrt provided parameters.
         * @returns Array of surcharge profile response objects.
         */
        getAllSurchargeProfileWithPagination: builder.query({
            query: (request: PaginationSearchSortingType) => {
                        return {
                            url: API_GET_SURCHARGE_PROFILES_By_Pagination,
                            method: 'POST',
                            body: JSON.stringify(request),
                        };
                    }
        }),

        /**
         * Fetches all dealers currently assigned with surcharge profiles.
         * @returns Array of dealer-surcharge profile mapping objects.
         */

        getAllDealersWithSurchargeProfile: builder.query<DealerSurchargeProfiles[], void>({
            query: () => API_GET_DEALER_SURCHARGE_PROFILES,
            providesTags: ['DealersWithSurcharge'],
            keepUnusedDataFor: 0,
        }),

                /**
         * Fetches dealer by ID currently assigned with surcharge profile.
         * @returns dealer-surcharge profile mapping object.
         */

        getDealerWithSurchargeById: builder.query<DealerSurchargeResponse, Number>({
            query: (dealerId: Number) => `${API_GET_DEALER_SURCHARGE_PROFILE_BY_DEALERID}?dealerId=${dealerId}`,
            providesTags: ['DealerWithSurcharge'],
            keepUnusedDataFor: 0,
        }),

        /**
         * Applies a selected surcharge profile to a given dealer.
         * @param request Contains DealerId and ProfileId to apply.
         * @returns EnableSurchargeResponse object with success status and details.
         */
        handleSurchargeForDealers: builder.mutation<HandleDealerSurchargeResponse[], HandleDealerSurchargeRequest>({
            query: (request: HandleDealerSurchargeRequest) => {
                return {
                    url: API_POST_HANDLE_SURCHARGE_FOR_DEALER,
                    method: 'POST',
                    body: request,
                }
            },
            invalidatesTags: ['DealersWithSurcharge','Dealers'],
        }),

        //  * Create Surcharge profile.
        //  * @returns response against created profile.
        //  */
        createNewSurchargeProfile: builder.mutation<CreateSurchargeProfileResponse, CreateSurchargeProfile>({
            query: (request: CreateSurchargeProfile) => {
                return {
                    url: API_POST_CREATE_SURCHARGE_PROFILE,
                    method: 'POST',
                    body: request,
                }
            }
        }),

         //  * Create Surcharge profile.
        //  * @returns response against created profile.
        //  */
        updateSurchargeProfile: builder.mutation<UpdateSurchargeProfileResponse, UpdateSurchargeProfile>({
            query:  (request: UpdateSurchargeProfile) => {
                return {
                    url: API_PUT_UPDATE_SURCHARGE_PROFILE + '/'+ request.id,
                    method: 'PUT',
                    body: request,
                }
            }
        }),

        deleteSurchargeProfile: builder.mutation<any, string>({
            query:  (request: string) => {
                return {
                    url: API_DELETE_SURCHARGE_PROFILE + '/'+ request,
                    method: 'DELETE'
                }
            }
        })
    }),
});

// Export hooks generated by RTK Query for component usage
export const {
    useGetPerseusPayOnboardedDealersQuery,
    useGetOnboardedDealersByMerchantQuery,
    useGetAllSurchargeProfilesQuery,
    useGetAllDealersWithSurchargeProfileQuery,
    useHandleSurchargeForDealersMutation,
    useGetDealerWithSurchargeByIdQuery,
    useGetAllSurchargeProfileWithPaginationQuery,
    useCreateNewSurchargeProfileMutation,
    useUpdateSurchargeProfileMutation,
    useDeleteSurchargeProfileMutation
} = dealerSurchargeApi;
